/**
 * The page that says this URL, recipe, batch or account is not here.
 *
 * One component rather than a sentence per screen, so a missing recipe and a
 * mistyped URL feel like the same product. Router-free except for
 * {@link RouteNotFound}: every other caller supplies the way out, which is how
 * the rest of the pages stay testable without a memory router.
 *
 * The record pages additionally read the workspace and the sync contexts, to
 * know whether "not in this workspace" is something this device can honestly
 * say yet — see {@link WhenSettled}. Both absences are tolerated, so a page
 * rendered without the app around it still answers as it always did.
 */

import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { OpeningSplash, WORKSPACE_SPLASH_LABEL } from '@/components/opening-splash';
import { BeerGlass } from '@/components/recipe/beer-glass';
import { Button } from '@/components/ui/button';
import { cmd } from '@/lib/keys';
import { useSharedRecipeLoading } from '@/store/workspace-context';
import { useWorkspaceSettled } from '@/sync/sync-context';

export interface NotFoundPageProps {
  readonly title?: string;
  readonly description?: string;
  /** The primary way out. A button, or a Link rendered as one. */
  readonly action?: ReactNode;
  /**
   * Mention the command palette. The unknown-URL page does; a missing recipe
   * already has a labelled button and does not need a second hint.
   */
  readonly suggestPalette?: boolean;
}

export function NotFoundPage({
  title = 'Not found',
  description = 'We could not find that page. It may have been deleted, or the link may be wrong.',
  action,
  suggestPalette = false,
}: NotFoundPageProps) {
  return (
    <div className="flex min-h-full w-full flex-col items-center justify-center px-4 py-16 text-center">
      <BeerGlass empty className="h-20 w-24" />
      <h1 className="mt-6 text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">{description}</p>
      {action !== undefined && <div className="mt-6">{action}</div>}
      {suggestPalette && (
        <p className="mt-4 text-xs text-muted-foreground">
          Or press{' '}
          <kbd className="rounded border border-border bg-muted px-1 font-mono text-[0.7rem]">
            {cmd('K')}
          </kbd>{' '}
          to jump to another page.
        </p>
      )}
    </div>
  );
}

/**
 * Unknown URL — the root route's `notFoundComponent`, and the router's default.
 *
 * This is the one caller that knows about routes: a mistyped path has no
 * "parent list" of its own, so the way out is the recipes index.
 */
export function RouteNotFound() {
  return (
    <NotFoundPage
      suggestPalette
      action={
        <Button asChild>
          <Link to="/recipes">Back to recipes</Link>
        </Button>
      }
    />
  );
}

/**
 * A record page's answer, held back until the workspace can be believed.
 *
 * "Not in this workspace" is a claim about what the store does *not* hold, and
 * a store that has not met the server yet cannot make it: a hydration is
 * allowed to open an empty workspace, and an empty workspace and a deleted
 * batch look exactly alike from here. The splash is the one the shell was
 * showing a moment earlier, so the wait reads as the app still opening rather
 * than as a second kind of loading.
 *
 * Wrapped around the record pages only. An unknown URL is not a claim about the
 * workspace, and {@link NotFoundPage} itself is used by callers — the admin
 * screens, the share pages — with nothing to wait for.
 */
function WhenSettled({
  children,
  waiting = false,
  slowly,
}: {
  readonly children: ReactNode;
  /** A reason of the page's own to keep waiting, on top of the workspace's. */
  readonly waiting?: boolean;
  /** What to show under the splash once `waiting` has held for {@link SLOW_WAIT_MS}. */
  readonly slowly?: ReactNode;
}) {
  const settled = useWorkspaceSettled();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!waiting) {
      setSlow(false);
      return;
    }
    const timer = setTimeout(() => setSlow(true), SLOW_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waiting]);
  if (!settled || waiting) {
    return (
      <OpeningSplash label={WORKSPACE_SPLASH_LABEL}>
        {waiting && slow ? slowly : null}
      </OpeningSplash>
    );
  }
  return <>{children}</>;
}

/**
 * How long a shared recipe may keep its page on the splash before the page
 * offers a way out.
 *
 * A room that has never answered on this device — the first visit, offline —
 * has nothing to show and no end in sight, and the mobile shell has no
 * pull-to-refresh to escape with. Eight seconds is well past a real first
 * pull and short of giving up on one.
 */
export const SLOW_WAIT_MS = 8_000;

/**
 * A recipe id that is not in this workspace.
 *
 * Or not *yet*: a shared recipe whose room has not answered is in no store on
 * this device, and is loading rather than missing (PLAN-COLLAB D-CO11c). Pass
 * the id and the page waits for it.
 */
export function RecipeNotFound({
  onBack,
  recipeId,
}: {
  readonly onBack: () => void;
  readonly recipeId?: string;
}) {
  const waiting = useSharedRecipeLoading(recipeId);
  return (
    <WhenSettled
      waiting={waiting}
      slowly={
        <>
          <p className="max-w-md text-center text-sm text-muted-foreground">
            This shared recipe is taking a while to open.
          </p>
          <Button type="button" variant="link" onClick={onBack}>
            Back to recipes
          </Button>
        </>
      }
    >
      <NotFoundPage
        title="Recipe not found"
        description="That recipe is not in this workspace. It may have been deleted, or this link may be from another account."
        action={
          <Button type="button" variant="secondary" onClick={onBack}>
            Back to recipes
          </Button>
        }
      />
    </WhenSettled>
  );
}

/** A batch id that is not in this workspace. */
export function BatchNotFound({
  onBack,
  backLabel = 'Back to batches',
}: {
  readonly onBack: () => void;
  readonly backLabel?: string;
}) {
  return (
    <WhenSettled>
      <NotFoundPage
        title="Batch not found"
        description="This batch is not in this workspace. It may have been deleted, or this link may be from another account."
        action={
          <Button type="button" variant="secondary" onClick={onBack}>
            {backLabel}
          </Button>
        }
      />
    </WhenSettled>
  );
}

/** A journal id that is not in this workspace. */
export function JournalNotFound({ onBack }: { readonly onBack: () => void }) {
  return (
    <WhenSettled>
      <NotFoundPage
        title="Journal not found"
        description="This journal is not in this workspace. It may have been deleted, or this link may be from another account."
        action={
          <Button type="button" variant="secondary" onClick={onBack}>
            Back to journals
          </Button>
        }
      />
    </WhenSettled>
  );
}

/** A blend id that is not in this workspace. */
export function BlendNotFound({ onBack }: { readonly onBack: () => void }) {
  return (
    <WhenSettled>
      <NotFoundPage
        title="Blend not found"
        description="This blending session is not in this workspace. It may have been deleted, or this link may be from another account."
        action={
          <Button type="button" variant="secondary" onClick={onBack}>
            Back to blending
          </Button>
        }
      />
    </WhenSettled>
  );
}
